import { FC, useEffect, useState } from 'react';
import { isEmpty } from 'lodash';
import { Form } from 'react-final-form';
import { GrSend } from 'react-icons/gr';
import { FormApi } from 'final-form';

import { Box, Button, RFFTextAreaInput, Loader, ActionConfirmation } from '@qwealth/qcore';
import { commonSubscription } from '@qwealth/kyc';

import { StyledConversationMessageBox } from './CommentNoteAttachmentSection.styled';
import TruncateTextByNumberOfChars from 'components/common/TruncateTextByNumberOfChars';
import { useAppDispatch, useAppSelector } from 'data/store';
import { formatRelativeDateInUserTimezone } from 'data/helpers/dateTimeHelper';
import { createTaskComment, deleteTaskComment, getTaskComments } from 'data/actions/taskComments';
import { selectTaskComments, selectTaskCommentsIsLoading } from 'data/selectors/taskCommentsSelectors';
import { DEFAULT_COMMENT } from 'data/helpers/taskHelper';
import { QTaskComment } from 'data/models/Activity';
import { validateComment } from 'data/validations/taskValidations';
import { selectUser } from 'data/selectors/userSelectors';

interface Props {
  taskId: number | undefined;
  setIsTaskModalHidden?: React.Dispatch<React.SetStateAction<boolean>>;
}

const ConversationFormSection: FC<Props> = ({ taskId, setIsTaskModalHidden }) => {
  const dispatch = useAppDispatch();
  const comments = useAppSelector(selectTaskComments);
  const isLoading = useAppSelector(selectTaskCommentsIsLoading);
  const { timeZone, email } = useAppSelector(selectUser);
  const isTheUserCommentAuthor = (commentAuthorEmail: string | undefined): boolean => !!commentAuthorEmail && commentAuthorEmail === email;

  const deleteComment = async (commentId: string | undefined) => {
    if (!commentId) return;
    await dispatch(deleteTaskComment(commentId));
  };

  useEffect(() => {
    if (taskId) {
      (async () => {
        await dispatch(getTaskComments(taskId));
      })();
    }
  }, [taskId, dispatch]);

  const initialFormData: QTaskComment = {
    ...DEFAULT_COMMENT,
  };

  const saveComment = async (formValues: QTaskComment, form: FormApi<QTaskComment, Partial<QTaskComment>>) => {
    if (!taskId) {
      return;
    }

    const newComment: QTaskComment = {
      ...formValues,
      taskId: taskId,
    };

    const createdComment: QTaskComment | undefined = await dispatch(createTaskComment(newComment));

    if (!createdComment) {
      return;
    }
    form.reset();
  };

  return (
    <>
      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center">
          <Loader />
        </Box>
      ) : (
        <Box display="flex" flexDirection="column" maxHeight="375px">
          <Box overflowY="auto" marginBottom="large" paddingRight="large">
            {isEmpty(comments) ? (
              <Box as="span" fontStyle="italic">
                You don't have any message.
              </Box>
            ) : (
              comments.map(comment => (
                <StyledConversationMessageBox key={comment?.uuid}>
                  <Box className="profile-icon-column">
                    <Box className="avatar">{`${comment.firstName?.substring(0, 1)}${comment.lastName?.substring(0, 1)}`}</Box>
                  </Box>
                  <Box className="username-row">{`${comment.firstName} ${comment.lastName}`}</Box>
                  <Box className="message-row">
                    <TruncateTextByNumberOfChars text={comment?.content ?? ''} maxLength={150} />
                  </Box>
                  <Box className="date-column">{formatRelativeDateInUserTimezone(comment?.postDatetime ?? '', timeZone)}</Box>
                  {isTheUserCommentAuthor(comment?.author) && (
                    <Box className="delete-button">
                      <ActionConfirmation
                        actionHandler={() => {
                          deleteComment(comment?.uuid);
                        }}
                        target={'delete this comment'}
                        setPreviousModalHidden={setIsTaskModalHidden}
                      />
                    </Box>
                  )}
                </StyledConversationMessageBox>
              ))
            )}
          </Box>
          <Form<QTaskComment>
            initialValues={initialFormData}
            onSubmit={(values, form) => saveComment(values, form)}
            validate={values => validateComment(values)}
          >
            {({ handleSubmit, submitting }) => {
              return (
                <Box display="flex" flexDirection="row" gap="default" alignItems="flex-end">
                  <Box display="flex" flexDirection="column" flex="1" marginBottom="small">
                    <RFFTextAreaInput
                      name="content"
                      subscription={commonSubscription}
                      inputFieldProps={{
                        minHeight: '100px',
                        style: { resize: 'none' },
                        placeholder: 'Write a comment...',
                      }}
                    />
                  </Box>
                  <Box flex="0">
                    <Button variant="icon" onClick={handleSubmit} disabled={submitting} size="default">
                      <GrSend />
                    </Button>
                  </Box>
                </Box>
              );
            }}
          </Form>
        </Box>
      )}
    </>
  );
};

export default ConversationFormSection;
